import React, { useState, useEffect, useRef, useCallback } from 'react';
import EventSystem from './EventSystem';
import MyCountdownClock from './myCountdownClock';
import moment from 'moment';
import './css/timerModule.css';
import GlobalFunction from './globalFunction';
import { Tile, LoggingIndicator } from './style/tile.style';

export default function TimerDisplayModule({ parameters, onSendMessage, enableModuleLogging, addLogEntry }) {
    const timerDisplayRef = useRef(null);
    const fontSizeCheckerRef = useRef(null);
    const timerIDRef = useRef(0);

    // Mutable ref holding values that EventSystem callbacks read synchronously
    const live = useRef({
        clockOffset: 0,
        localClockOffset: 0,
        timeMasterClockOffset: parameters.config.timeMasterClockOffset,
        timerCount: 0,
        currentCount: 0,
        timerStatus: '',
        timeSyncCounter: 0,
        onStartupChecked: false,
        postMessage: '',
        data: { timerParams: { secs: 0 } },
        containerWidth: 0,
        maxTextHeight: 0,
    });
    // Keep these current on every render — clockOffset updates on every ping, not just at sync-cycle end
    live.current.timeMasterClockOffset = parameters.config.timeMasterClockOffset;
    live.current.clockOffset = parameters.config.clockOffset;

    const [timerMessage, setTimerMessage] = useState('');
    const [containerHeight, setContainerHeight] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);
    const [data, setData] = useState({ timerParams: { secs: 0 } });
    const [timerStatus, setTimerStatus] = useState('');
    const [timerCount, setTimerCount] = useState(0);
    const [timerSize, setTimerSize] = useState(0);
    const [loggingModule, setLoggingModule] = useState(false);
    const [highlight, setHighlight] = useState(false);
    const [fontSize, setFontSize] = useState(36);
    const [clockOffset, setClockOffset] = useState(0);

    function measureContainer() {
        const el = timerDisplayRef.current.parentElement.parentElement;
        const h = parseInt(el.offsetHeight - 2, 10);
        const w = parseInt(el.offsetWidth - 2, 10);
        let tSize = h < w ? h : w;
        if (tSize === 0) tSize = parseInt(parameters.containerHeight.container, 10);
        const bottomGap = h - tSize;
        const maxTH = tSize * 0.4 + bottomGap;
        setContainerHeight(h);
        setContainerWidth(w);
        setTimerSize(tSize);
        live.current.containerWidth = w;
        live.current.maxTextHeight = maxTH;
        return { w, maxTH };
    }

    useEffect(() => {
        enableModuleLogging();
        measureContainer();

        const moduleId = parameters.config.event_screen_module_id;
        EventSystem.subscribe('TimerControlModule', showMessage);
        EventSystem.subscribe('localClockOffset', updateLocalClockOffset);
        EventSystem.subscribe('logStatus' + moduleId, setLoggingModule);
        EventSystem.subscribe('highlightModule' + moduleId, setHighlight);
        EventSystem.subscribe('updatedClockOffset', newClockOffset);

        return () => {
            EventSystem.unsubscribe('TimerControlModule', showMessage);
            EventSystem.unsubscribe('localClockOffset', updateLocalClockOffset);
            EventSystem.unsubscribe('logStatus' + moduleId, setLoggingModule);
            EventSystem.unsubscribe('highlightModule' + moduleId, setHighlight);
            EventSystem.unsubscribe('updatedClockOffset', newClockOffset);
        };
    }, []);

    useEffect(() => {
        if (timerDisplayRef.current) measureContainer();
    }, [parameters.containerHeight.container]);

    function updateLocalClockOffset(secs) {
        live.current.localClockOffset = secs; // seconds; getAdjustedDate multiplies by 1000 internally
    }

    function newClockOffset(offsetData) {
        addLogEntry('ClockOffset # ' + offsetData.timeSyncCounter + ' received, was: ' + live.current.clockOffset + ' now: ' + offsetData.clockOffset);
        if (live.current.timerStatus === 'start') {
            addLogEntry('we received a new clockoffset when a timer was running');
        }
        live.current.clockOffset = offsetData.clockOffset;
        live.current.timeSyncCounter = offsetData.timeSyncCounter;
        setClockOffset(offsetData.clockOffset);

        if (offsetData.timeSyncCounter > 0 && !live.current.onStartupChecked) {
            onSendMessage({
                action: 'getRunningTimer',
                sourceModuleName: 'TimerControlModule',
                event_grouping_seq: parameters.config.event_grouping_seq,
                customerID: parameters.userProfile.customer_id,
                siteID: parameters.userProfile.site_id,
                eventID: parameters.config.event_id,
            });
            live.current.onStartupChecked = true;
        }
    }

    function showMessage(data) {
        if (data.action === 'kill') {
            EventSystem.publish('TimerDisplayModule', 'stop');
            clearTimeout(timerIDRef.current);
            live.current.timerStatus = 'kill';
            setTimerStatus('kill');
            setTimerMessage('');
            addLogEntry('Stopped');
        } else if (data.action === 'start') {
            const newCount = live.current.timerCount + 1;
            live.current.timerCount = newCount;
            live.current.currentCount = newCount;

            EventSystem.publish('TimerDisplayModule', 'start');
            clearTimeout(timerIDRef.current);

            const originalEndTime = data.timerParams.startTimeAtOrigin + (data.timerParams.secs * 1000);
            const adjustedDate = GlobalFunction.getAdjustedDate(live.current.localClockOffset, {
                clockOffset: live.current.clockOffset,
                timeMasterClockOffset: live.current.timeMasterClockOffset,
            });
            const ourAdjustedTime = adjustedDate.getTime();
            data.timerParams.adjustedSeconds = (originalEndTime - ourAdjustedTime) / 1000;
            data.timerParams.endTimeMs = originalEndTime; // absolute end time; clock recomputes from this on every tick

            if (data.timerParams.adjustedSeconds < 0) return;

            live.current.postMessage = data.timerParams.instructions;
            live.current.data = data;
            live.current.timerStatus = 'start';

            setData(data);
            setTimerCount(newCount);
            setTimerStatus('start');
            setTimerMessage(data.timerParams.description);

            const logEntry = 'Start time at origin: ' + moment(data.timerParams.startTimeAtOrigin).format('HH:mm:ss.SSS') +
                '. Adjusted start time local: ' + moment(ourAdjustedTime).format('HH:mm:ss.SSS');
            addLogEntry('Starting: ' + data.timerParams.description);
            addLogEntry(logEntry);
            addLogEntry('Timer duration adjusted from ' + data.timerParams.secs + ' to ' + data.timerParams.adjustedSeconds + ' seconds');
            addLogEntry('End time at origin: ' + moment(originalEndTime).format('HH:mm:ss.SSS'));
            addLogEntry(live.current.timeMasterClockOffset > 0 ? 'Time master has been set' : 'No time master set');
            addLogEntry('Offsets: Device to server-' + live.current.clockOffset + ', TimeMaster to server-' + live.current.timeMasterClockOffset + ', Propogationdelay-' + live.current.localClockOffset + 's');

            const fs = GlobalFunction.getFontSizeVariableHeight(data.timerParams.description, live.current.containerWidth - 10, live.current.maxTextHeight, fontSizeCheckerRef.current);
            setFontSize(fs);
        }
    }

    function onComplete() {
        if (live.current.currentCount === live.current.timerCount) {
            const ourAdjustedTime = Date.now() + live.current.clockOffset - live.current.timeMasterClockOffset + (live.current.localClockOffset * 1000);
            addLogEntry('Timer finished: ' + moment(ourAdjustedTime).format('HH:mm:ss.SSS'));

            live.current.timerStatus = 'ended';
            setTimerStatus('ended');

            if (live.current.postMessage.length > 0) {
                const postFs = GlobalFunction.getFontSizeVariableHeight(live.current.postMessage, live.current.containerWidth - 10, live.current.maxTextHeight, fontSizeCheckerRef.current);
                setFontSize(postFs);
                setTimerMessage(live.current.postMessage);
                if (live.current.data.other_config?.autoHide) {
                    timerIDRef.current = setTimeout(() => {
                        setTimerMessage('');
                        EventSystem.publish('TimerDisplayModule', 'stop');
                    }, live.current.data.other_config.autoHideSeconds * 1000);
                }
            }
        }
    }

    function onMidcall() {
        EventSystem.publish('FlashScreen', {
            sourceModuleName: 'FlashScreen',
            event_grouping_seq: 0,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
            action: 'start',
        });
    }

    // Stable callback — reads live refs on every call so it always returns the current adjusted time
    const getAdjustedNowMs = useCallback(() =>
        Date.now() + live.current.clockOffset - live.current.timeMasterClockOffset + (live.current.localClockOffset * 1000),
    []);

    const messageFontSize = fontSize + 'px';
    const containerStyle = { position: 'absolute', top: '0px', left: '0px', width: containerWidth + 'px', height: containerHeight + 'px' };
    const messageStyle = {
        position: 'absolute', zIndex: 1, fontSize: messageFontSize, lineHeight: messageFontSize,
        color: '#dff9fb', whiteSpace: 'pre-line', wordBreak: 'normal',
        textAlign: 'center', bottom: '5px', left: '0px', width: '100%',
    };

    return (
        <Tile ref={timerDisplayRef} highlight={highlight} style={containerStyle}>
            <div style={messageStyle}>{timerMessage}</div>
            <div style={{ margin: 'auto 0', position: 'relative', bottom: 0 }}>
                {timerStatus === 'start' && (
                    <MyCountdownClock
                        key="clock"
                        seconds={data.timerParams.adjustedSeconds}
                        endTimeMs={data.timerParams.endTimeMs}
                        getAdjustedNowMs={getAdjustedNowMs}
                        midcallSeconds={data.flash ? data.seconds : 0}
                        color="#ec0b43"
                        alpha={0.9}
                        weight={timerSize * 0.2}
                        showMilliseconds={true}
                        onComplete={onComplete}
                        onMidcall={onMidcall}
                        timerCount={timerCount}
                        size={timerSize * 0.9} />
                )}
            </div>
            <div style={{ position: 'fixed', top: '-2000px', left: '-1000px', wordBreak: 'normal', backgroundColor: 'pink', whiteSpace: 'pre-line' }}
                ref={fontSizeCheckerRef} />
            <LoggingIndicator logStatus={loggingModule} />
        </Tile>
    );
}
